/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, ChangeEvent, useContext, useEffect } from 'react';
import {
  Modal, Typography, Button, Grid, DialogTitle, Dialog, DialogContent,
  IconButton, Snackbar, Alert, Tabs, Tab, Box, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { UserContext } from '../../../components/shared/useUser';
import { exportFile } from '../../../utils';
import useLogError from '../../../hooks/useLogError';
import CustomPagination from '../../../components/forms/Pagination';
import * as XLSX from 'xlsx';
import { X } from './../../../assets';
import { Loader } from '../../../components/ui/Loader';

type ValidationErrorItem = {
  rowNumber?: number;
  recipeSku?: string;
  ingredientSku?: string;
  weight?: number;
  quantity?: number;
};

type ValidationErrors = {
  ProductNotFound: ValidationErrorItem[];
  MissingWeightAndQuantity: ValidationErrorItem[];
  ProductHasNoWeightButIngredientHasWeight: ValidationErrorItem[];
  DataError: ValidationErrorItem[];
};

type MergeWarningItem = {
  recipeSku?: string;
  storeNumbers?: string; // contains comma-separated store numbers
};

const VALIDATION_TABS = [
  'DataError',
  'ProductNotFound',
  'MissingWeightAndQuantity',
  'ProductHasNoWeightButIngredientHasWeight',
] as const;

const TAB_DESCRIPTION_KEYS = [
  'DataErrorDesc',
  'ProductNotFoundDesc',
  'MissingWeightAndQuantityDesc',
  'ProductHasNoWeightButIngredientHasWeightDesc',
] as const;

const ERRORS_PAGE_SIZE = 5;

interface CreateBulkManageProps {
  open: boolean;
  handleClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSuccess: (message: string) => void;
}

const CreateBulkManage: React.FC<CreateBulkManageProps> = ({ open, handleClose, onSuccess }) => {
  const { t } = useTranslation('bulk');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { exportBulkRecipes, uploadBulkRecipes } = useNswagClient();
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const [showUploadSuccessMessage, setShowUploadSuccessMessage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null);
  const [mergeWarnings, setMergeWarnings] = useState<MergeWarningItem[] | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tabPages, setTabPages] = useState<Record<number, number>>({ 0: 0, 1: 0, 2: 0, 3: 0 });
  const [mergeWarningsPage, setMergeWarningsPage] = useState(0);
  const expectedColumns = ['Stores and Franchises', 'Country', 'Recipe SKU', 'Recipe Name', 'Category', 'Ingredient SKU', 'Ingredient Name', 'Weight (G)', 'Primary UOM Quantity'];

  useEffect(() => {
    if (!open) {
      setErrorMessage(null);
      setSuccessMessage(null);
      setSelectedFile(null);
      setValidationErrors(null);
      setMergeWarnings(null);
      setActiveTab(0);
      setTabPages({ 0: 0, 1: 0, 2: 0, 3: 0 });
      setMergeWarningsPage(0);
    }
  }, [open]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(null);
    setValidationErrors(null);
    setMergeWarnings(null);
    const file = event.target.files?.[0] ?? null;

    if (file) {
      const fileType = file.name.split('.').pop();
      if (fileType !== 'xlsx') {
        setErrorMessage(t('errorInvalidFile'));
        setSelectedFile(null);
        return;
      }

      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[];

        const hasAllColumns = expectedColumns.every(col => headers.includes(col));
        if (!hasAllColumns) {
          setSuccessMessage(null);
          setErrorMessage(t('errorInvalidTemplate'));
          setSelectedFile(null);
        } else {
          setSelectedFile(file);
          setSuccessMessage(t('fileSelected', { name: file.name }));
        }
      } catch (error) {
        setSuccessMessage(null);
        setErrorMessage(t('errorReadingFile'));
        setSelectedFile(null);
        logError(error);
      }

      event.target.value = '';
    }
  };

  const handleDownloadSample = (e: any) => {
    e.preventDefault();
    if (selectedStore?.storeNumber) {
      setIsExporting(true);
      exportBulkRecipes(selectedStore.storeNumber)
        .then((response) => {
          if (response) {
            exportFile(
              response.data,
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              `Recipes-Bulk-${selectedStore?.franchiseName}.xlsx`,
            );
          }
        })
        .catch(logError)
        .finally(() => {
          setIsExporting(false);
        });
    }
  };

  const handleSave = async () => {
    if (!selectedFile) {
      setErrorMessage(t('errorInvalidFile'));
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setValidationErrors(null);

    try {
      const storeNumber = selectedStore?.storeNumber ?? '';
      const response = await uploadBulkRecipes(storeNumber, selectedFile, mergeWarnings ? true : false);

      // Check if response contains merge warnings (returned as 200)
      if (response && response.StoreLevelMergedToFranchise?.length > 0) {
        setMergeWarnings(response.StoreLevelMergedToFranchise.map(
          (x: any): MergeWarningItem => ({
            recipeSku: x.recipeSku,
            storeNumbers: x.ingredientSku, // map ingredientSku into storeNumbers
          }),
        ));
      } else {
        setShowUploadSuccessMessage(true);
        setSelectedFile(null);
        setSuccessMessage(null);
        setMergeWarnings(null);
        onSuccess('Recipes updated successfully!');
      }
    } catch (error: any) {
      let data = error?.responseData ?? error?.response?.data ?? error?.result;

      if (!data && typeof error?.message === 'string' && error.message.startsWith('Upload failed: ')) {
        try {
          data = JSON.parse(error.message.slice('Upload failed: '.length));
        } catch {
          // Ignore JSON parsing errors and fallback to generic error message
        }
      }
      if (
        data &&
        (data.ProductNotFound !== undefined ||
          data.MissingWeightAndQuantity !== undefined ||
          data.ProductHasNoWeightButIngredientHasWeight !== undefined ||
          data.DataError !== undefined)
      ) {
        setValidationErrors(data as ValidationErrors);
        setActiveTab(0);
        setTabPages({ 0: 0, 1: 0, 2: 0, 3: 0 });
      } else if (data && data.StoreLevelMergedToFranchise !== undefined && data.StoreLevelMergedToFranchise.length > 0) {
        setMergeWarnings(data.StoreLevelMergedToFranchise as MergeWarningItem[]);
        setMergeWarningsPage(0);
      } else {
        logError(error);
        setErrorMessage(t('errorUploadingFile'));
      }
      setSuccessMessage(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setShowUploadSuccessMessage(false);
  };

  const getTabItems = (tabIndex: number): ValidationErrorItem[] => {
    if (!validationErrors) return [];
    return validationErrors[VALIDATION_TABS[tabIndex]] ?? [];
  };

  const getPagedItems = (tabIndex: number): ValidationErrorItem[] => {
    const items = getTabItems(tabIndex);
    const page = tabPages[tabIndex] ?? 0;
    return items.slice(page * ERRORS_PAGE_SIZE, (page + 1) * ERRORS_PAGE_SIZE);
  };

  const getTotalPages = (tabIndex: number): number => {
    return Math.ceil(getTabItems(tabIndex).length / ERRORS_PAGE_SIZE);
  };

  const getPagedMergeWarnings = (): MergeWarningItem[] => {
    if (!mergeWarnings) return [];
    return mergeWarnings.slice(mergeWarningsPage * ERRORS_PAGE_SIZE, (mergeWarningsPage + 1) * ERRORS_PAGE_SIZE);
  };

  const getMergeWarningsTotalPages = (): number => {
    return Math.ceil((mergeWarnings?.length ?? 0) / ERRORS_PAGE_SIZE);
  };

  const hasMergeWarnings = !!mergeWarnings && mergeWarnings.length > 0;
  const hasValidationErrors = !!validationErrors;
  const showInitialUploadActions = !hasMergeWarnings && !hasValidationErrors;

  const topLevelTitle = hasMergeWarnings
    ? t('mergeWarningTitle')
    : hasValidationErrors
      ? t('validationTitle')
      : t('modalTitle');

  const topLevelDescription = hasMergeWarnings
    ? t('mergeWarningDescription')
    : hasValidationErrors
      ? t('validationDescription')
      : t('modalDescription');

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Dialog open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <InventoryIcon
            sx={{
              mr: 1,
              color: 'black',
              fontSize: '20px',
              width: '20px',
              height: '24px',
            }}
          />
          <Typography variant="textXL"
            sx={{ display: 'flex', flex: 1, justifyContent: 'left', alignItems: 'center' }}>
            {topLevelTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ p: 0 }}
          >
            <X />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography
            id="modal-description"
            sx={{
              textAlign: 'left',
              mb: 3,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              color: 'inherit',
            }}
          >
            {topLevelDescription}
          </Typography>

          {showInitialUploadActions && (
            <Grid item
              xs={12}
              sx={{ display: 'flex', justifyContent: 'left', columnGap: '10px' }}>
              <Button size='lg'
                variant='secondary'
                color="secondary"
                onClick={handleDownloadSample}
                disabled={isExporting}
              >
                {isExporting ? t('exporting') : t('download')}
              </Button>
              <Button size='lg'
                color="secondary"
                onClick={() => document.getElementById('fileInput')?.click()}
                sx={{ marginRight: '8px' }}
              >
                {t('upload')}
              </Button>
            </Grid>
          )}

          {showInitialUploadActions && (
            <input aria-hidden
              type="file"
              id="fileInput"
              accept=".xlsx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          )}

          {errorMessage && (
            <Typography
              color="error"
              sx={{ mt: 2, fontSize: 18 }}
            >
              {errorMessage}
            </Typography>
          )}

          {isSaving && !mergeWarnings && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 18, color: 'text.secondary' }}>
                {t('validating')}
              </Typography>
            </Box>
          )}

          {successMessage && !isSaving && !validationErrors && !mergeWarnings && (
            <Box sx={{ mt: 5, ml: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 23 }} />
              <Typography sx={{ fontSize: 16, color: 'success.main' }}>
                {successMessage}
              </Typography>
            </Box>
          )}

          {/* Merge warnings panel */}
          {hasMergeWarnings && (
            <Box sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        '& .MuiTableCell-head': {
                          fontSize: '0.7rem',
                          height: '25px',
                          fontWeight: 600,
                          padding: '4px 8px',
                          lineHeight: 1.2,
                        },
                      }}>
                      <TableCell>{t('colRecipeSku')}</TableCell>
                      <TableCell>{t('mergeAffectedStores')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getPagedMergeWarnings().map((item, i) => (
                      <TableRow key={i}
                        sx={{
                          '& .MuiTableCell-body': {
                            fontSize: '0.7rem',
                            height: '30px',
                            padding: '4px 8px',
                            lineHeight: 1.2,
                          }, 
                        }}>
                        <TableCell>{item.recipeSku ?? '—'}</TableCell>
                        <TableCell>{item.storeNumbers ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {getMergeWarningsTotalPages() > 1 && (
                <CustomPagination
                  page={mergeWarningsPage}
                  setPage={setMergeWarningsPage}
                  maxPages={getMergeWarningsTotalPages()}
                  breakpointForChangeDisplay={120000}
                  useNoDataMessage={false}
                />
              )}
            </Box>
          )}

          {/* Validation errors panel */}
          {hasValidationErrors && (
            <>
              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{ flex: 1 }}
                >
                  {VALIDATION_TABS.map((key) => (
                    <Tab
                      key={key}
                      label={`${t(key as any)} (${(validationErrors[key] ?? []).length})`}
                      sx={{ fontSize: '0.78rem', textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5, fontFamily: 'inherit' }}>
                      {VALIDATION_TABS.map((key, i) => (
                        <Box key={key}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: 'inherit' }}>
                            {t(key as any)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'inherit' }}>
                            {t(TAB_DESCRIPTION_KEYS[i] as any)}
                          </Typography>
                          {i < VALIDATION_TABS.length - 1 && (
                            <Box sx={{ height: '12px' }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  }
                  arrow
                  placement="left"
                >
                  <IconButton size="medium" 
                    sx={{ ml: 1, color: 'text.secondary' }}>
                    <InfoOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {VALIDATION_TABS.map((key, tabIndex) => (
                <Box
                  key={key}
                  hidden={activeTab !== tabIndex}
                  sx={{ mt: 2 }}
                >
                  {activeTab === tabIndex && (
                    <>
                      {getTabItems(tabIndex).length === 0 ? (
                        <Typography sx={{ py: 2, color: 'text.secondary', fontSize: '0.85rem' }}>
                          {t('noValidationErrors')}
                        </Typography>
                      ) : (
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0 }}>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow
                                  sx={{
                                    '& .MuiTableCell-head': {
                                      fontSize: '0.7rem',
                                      height: '25px',
                                      fontWeight: 600,
                                      padding: '4px 8px',
                                      lineHeight: 1.2,
                                    },
                                  }}>
                                  <TableCell>{t('colRowNumber')}</TableCell>
                                  <TableCell>{t('colRecipeSku')}</TableCell>
                                  <TableCell>{tabIndex === 0 ? t('colStoreFranchise') : t('colIngredientSku')}</TableCell>
                                  <TableCell>{t('colWeight')}</TableCell>
                                  <TableCell>{t('colQuantity')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {getPagedItems(tabIndex).map((item, i) => (
                                  <TableRow key={i}
                                    sx={{
                                      '& .MuiTableCell-body': {
                                        fontSize: '0.7rem',
                                        height: '30px',
                                        padding: '4px 8px',
                                        lineHeight: 1.2,
                                      },
                                    }}>
                                    <TableCell>{item.rowNumber ?? '—'}</TableCell>
                                    <TableCell>{item.recipeSku ?? '—'}</TableCell>
                                    <TableCell>{item.ingredientSku ?? '—'}</TableCell>
                                    <TableCell>{item.weight ?? '—'}</TableCell>
                                    <TableCell>{item.quantity ?? '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {getTotalPages(tabIndex) > 1 && (
                            <CustomPagination
                              page={tabPages[tabIndex] ?? 0}
                              setPage={(p) => setTabPages(prev => ({ ...prev, [tabIndex]: p }))}
                              maxPages={getTotalPages(tabIndex)}
                              breakpointForChangeDisplay={120000}
                              useNoDataMessage={false}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </>
          )}

          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'right',
              columnGap: { xs: '8px', sm: '10px' },
              mt: 3,
            }}
          >
            <Button
              size="lg"
              variant="secondary"
              onClick={handleClose}
              sx={{ marginTop: { xs: '8px', sm: '0' } }}
            >
              {hasValidationErrors ? t('close') : t('cancel')}
            </Button>
            {!hasValidationErrors && (
              <Button
                size="lg"
                onClick={handleSave}
                disabled={!selectedFile || !!errorMessage || isSaving}
                sx={{ marginTop: { xs: '8px', sm: '0' } }}
              >
                {isSaving ? <Loader /> : mergeWarnings ? t('confirmMerge') : t('save')}
              </Button>
            )}
          </Grid>
        </DialogContent>

        {/* Success Snackbar */}
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={showUploadSuccessMessage}
          onClose={handleCloseSnackbar}
          autoHideDuration={5000}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {t('fileUploadSuccess')}
          </Alert>
        </Snackbar>
      </Dialog>
    </Modal>
  );
};

export default CreateBulkManage;